import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  FiUsers,
  FiAward,
  FiShield,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiUserMinus,
  FiUserPlus,
  FiChevronDown,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiAlertTriangle,
  FiVolumeX,
  FiVolume2,
  FiSlash,
  FiUserCheck,
  FiMoreVertical,
} from "react-icons/fi";
import communityService from "../../services/communityService";
import { WarnModal, MuteModal, BanModal } from "./moderation/ModerationModals";
import "./styles/index.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "default",
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          iconBg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
          iconColor: "#ef4444",
          btnBg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        };
      case "warning":
        return {
          iconBg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          iconColor: "#f59e0b",
          btnBg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        };
      default:
        return {
          iconBg: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
          iconColor: "#00c853",
          btnBg: "linear-gradient(135deg, #00c853 0%, #00a844 100%)",
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <AnimatePresence>
      <motion.div
        className="collab-comm-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div
          className="collab-comm-confirm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="collab-comm-confirm-header">
            <div
              className="collab-comm-confirm-icon"
              style={{ background: typeStyles.iconBg }}
            >
              <FiAlertTriangle
                size={24}
                style={{ color: typeStyles.iconColor }}
              />
            </div>
            <h3>{title}</h3>
            <button className="collab-comm-confirm-close" onClick={onClose}>
              <FiX size={18} />
            </button>
          </div>

          <div className="collab-comm-confirm-body">
            <p>{message}</p>
          </div>

          <div className="collab-comm-confirm-footer">
            <motion.button
              className="collab-comm-confirm-cancel"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {cancelText}
            </motion.button>
            <motion.button
              className="collab-comm-confirm-action"
              onClick={onConfirm}
              style={{ background: typeStyles.btnBg }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {confirmText}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const CommunityMembers = ({
  communityId,
  preview = false,
  previewData,
  community,
  userMembership,
}) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [actionLoading, setActionLoading] = useState({});

  // Moderation Modals State
  const [warnModal, setWarnModal] = useState({ isOpen: false, member: null });
  const [muteModal, setMuteModal] = useState({ isOpen: false, member: null });
  const [banModal, setBanModal] = useState({ isOpen: false, member: null });

  // Member restrictions state (for dynamic Mute/Unmute, Ban/Unban)
  const [memberRestrictions, setMemberRestrictions] = useState({});
  const [restrictionsLoading, setRestrictionsLoading] = useState(false);

  // Active dropdown menu (for three-dot actions)
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "default",
  });

  // Get current user's role in the community
  const getCurrentUserRole = () => {
    return userMembership?.role || community?.userMembership?.role || "member";
  };

  // Permission checks
  const getPermissions = (currentUserRole, targetMemberRole) => {
    const isCreator = currentUserRole === "creator";
    const isModerator = currentUserRole === "moderator";

    return {
      canPromote: isCreator && targetMemberRole === "member",
      canDemote: isCreator && targetMemberRole === "moderator",
      canRemove:
        (isCreator || (isModerator && targetMemberRole === "member")) &&
        targetMemberRole !== "creator",
      // Moderation actions - moderators and creators can warn/mute/ban members
      canWarn:
        (isCreator || isModerator) &&
        targetMemberRole === "member",
      canMute:
        (isCreator || isModerator) &&
        targetMemberRole === "member",
      canBan:
        (isCreator || isModerator) &&
        targetMemberRole === "member",
    };
  };

  useEffect(() => {
    fetchMembers();
  }, [communityId, searchTerm, roleFilter, currentPage, previewData]);

  const fetchMembers = async () => {
    try {
      setLoading(true);

      // If preview data contains moderators/creator info, use it for preview mode
      if (preview && previewData?.moderators) {
        const moderatorMembers = [];

        // Add creator
        if (previewData.moderators.creator) {
          moderatorMembers.push({
            _id: "creator",
            name: previewData.moderators.creator.name,
            profilePhotoUrl: previewData.moderators.creator.profilePhotoUrl,
            role: "creator",
            joinedAt: previewData.community?.createdAt,
            lastActive: new Date().toISOString(),
          });
        }

        // Add moderators
        if (previewData.moderators.moderators) {
          previewData.moderators.moderators.forEach((mod, index) => {
            const moderatorInfo = {
              _id: mod._id || `mod_${index}`,
              name:
                mod.user?.name ||
                mod.userInfo?.name ||
                mod.name ||
                `Moderator ${index + 1}`,
              profilePhotoUrl:
                mod.user?.profilePhotoUrl ||
                mod.userInfo?.profilePhotoUrl ||
                mod.profilePhotoUrl,
              role: "moderator",
              joinedAt:
                mod.addedAt || mod.joinedAt || previewData.community?.createdAt,
              lastActive: new Date().toISOString(),
              username:
                mod.user?.username || mod.userInfo?.username || mod.username,
              bio: mod.user?.bio || mod.userInfo?.bio || mod.bio,
            };
            moderatorMembers.push(moderatorInfo);
          });
        }

        // Sort by role
        const sortedPreviewMembers = moderatorMembers.sort((a, b) => {
          const roleOrder = { creator: 0, moderator: 1, member: 2 };
          return roleOrder[a.role] - roleOrder[b.role];
        });

        setMembers(sortedPreviewMembers.slice(0, 6));
        setTotalMembers(
          previewData.community?.memberCount || moderatorMembers.length
        );
        setTotalPages(1);
        setLoading(false);
        return;
      }

      // Regular API call for full member list
      const filters = {
        page: currentPage,
        limit: preview ? 6 : 20,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter !== "all" && { role: roleFilter }),
      };

      const response = await communityService.getCommunityMembers(
        communityId,
        filters
      );

      if (response.success && response.data) {
        const sortedMembers = (response.data.members || []).sort((a, b) => {
          const roleOrder = { creator: 0, moderator: 1, member: 2 };
          return roleOrder[a.role] - roleOrder[b.role];
        });

        setMembers(sortedMembers);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalMembers(response.data.pagination?.totalItems || 0);
      } else {
        setMembers([]);
        setTotalMembers(0);
        setTotalPages(1);
      }
    } catch (error) {
setMembers([]);
      setTotalMembers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch restrictions for all members (for moderators only) - performance optimized
  const fetchMemberRestrictions = useCallback(async (membersList) => {
    const currentUserRole = getCurrentUserRole();
    if (!["creator", "moderator"].includes(currentUserRole)) return;
    if (!membersList || membersList.length === 0) return;

    // Only fetch for regular members (not mods/creators)
    const regularMembers = membersList.filter(m => m.role === "member");
    if (regularMembers.length === 0) return;

    setRestrictionsLoading(true);
    
    // Batch fetch with Promise.allSettled for resilience
    const results = await Promise.allSettled(
      regularMembers.map(async (member) => {
        try {
          const response = await communityService.getUserRestrictions(communityId, member._id);
          const restrictions = response.data?.restrictions || response.restrictions || [];
          const active = restrictions.filter(r => r.isActive);
          return { memberId: member._id, restrictions: active };
        } catch {
          return { memberId: member._id, restrictions: [] };
        }
      })
    );

    const newMap = {};
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        const { memberId, restrictions } = result.value;
        const mute = restrictions.find(r => r.restrictionType === "mute");
        const ban = restrictions.find(r => r.restrictionType === "ban");
        newMap[memberId] = {
          isMuted: !!mute,
          isBanned: !!ban,
          muteRestriction: mute || null,
          banRestriction: ban || null,
        };
      }
    });

    setMemberRestrictions(newMap);
    setRestrictionsLoading(false);
  }, [communityId, getCurrentUserRole]);

  // Fetch restrictions when members change - FIXED: use stable dependency
  const memberIdsKey = useMemo(
    () => members.filter(m => m.role === "member").map(m => m._id).sort().join(","),
    [members]
  );
  
  useEffect(() => {
    if (members.length > 0 && !preview && memberIdsKey) {
      fetchMemberRestrictions(members);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, memberIdsKey, preview]);

  // Unmute handler
  const handleUnmute = async (memberId, restrictionId, memberName) => {
    setActionLoading((prev) => ({ ...prev, [memberId]: "unmuting" }));
    try {
      await communityService.removeRestriction(communityId, restrictionId, "Restriction lifted");
      toast.success(`${memberName} has been unmuted`);
      // Refresh restrictions
      fetchMemberRestrictions(members);
    } catch (error) {
      toast.error(error.message || "Failed to unmute user");
    } finally {
      setActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[memberId];
        return newState;
      });
    }
  };

  // Unban handler
  const handleUnban = async (memberId, restrictionId, memberName) => {
    setActionLoading((prev) => ({ ...prev, [memberId]: "unbanning" }));
    try {
      await communityService.removeRestriction(communityId, restrictionId, "Ban lifted");
      toast.success(`${memberName} has been unbanned`);
      // Refresh restrictions
      fetchMemberRestrictions(members);
    } catch (error) {
      toast.error(error.message || "Failed to unban user");
    } finally {
      setActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[memberId];
        return newState;
      });
    }
  };

  // Modal helpers
  const showConfirmation = (title, message, onConfirm, type = "default") => {
    setConfirmationModal({ isOpen: true, title, message, onConfirm, type });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "default",
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };

  // Member management functions
  const handlePromoteMember = async (memberId, memberName) => {
    showConfirmation(
      "Promote Member",
      `Are you sure you want to promote ${memberName} to moderator? They will gain additional permissions.`,
      async () => {
        setActionLoading((prev) => ({ ...prev, [memberId]: "promoting" }));
        try {
          const response = await communityService.promoteMember(
            communityId,
            memberId
          );
          if (response.success) {
            setMembers((prev) =>
              prev.map((member) =>
                member._id === memberId
                  ? { ...member, role: "moderator" }
                  : member
              )
            );
          }
        } catch (error) { /* ignored */ } finally {
          setActionLoading((prev) => {
            const newState = { ...prev };
            delete newState[memberId];
            return newState;
          });
        }
      },
      "warning"
    );
  };

  const handleDemoteMember = async (memberId, memberName) => {
    showConfirmation(
      "Demote Moderator",
      `Are you sure you want to demote ${memberName} to a regular member?`,
      async () => {
        setActionLoading((prev) => ({ ...prev, [memberId]: "demoting" }));
        try {
          const response = await communityService.demoteModerator(
            communityId,
            memberId
          );
          if (response.success) {
            setMembers((prev) =>
              prev.map((member) =>
                member._id === memberId ? { ...member, role: "member" } : member
              )
            );
          }
        } catch (error) { /* ignored */ } finally {
          setActionLoading((prev) => {
            const newState = { ...prev };
            delete newState[memberId];
            return newState;
          });
        }
      },
      "warning"
    );
  };

  const handleRemoveMember = async (memberId, memberName) => {
    showConfirmation(
      "Remove Member",
      `Are you sure you want to remove ${memberName} from the community? This cannot be undone.`,
      async () => {
        setActionLoading((prev) => ({ ...prev, [memberId]: "removing" }));
        try {
          const response = await communityService.removeMember(
            communityId,
            memberId
          );
          if (response.success) {
            setMembers((prev) =>
              prev.filter((member) => member._id !== memberId)
            );
            setTotalMembers((prev) => prev - 1);
          }
        } catch (error) { /* ignored */ } finally {
          setActionLoading((prev) => {
            const newState = { ...prev };
            delete newState[memberId];
            return newState;
          });
        }
      },
      "danger"
    );
  };

  // Date formatting
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Active now";
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return formatDate(dateString);
  };

  // Role configuration
  const getRoleConfig = (role) => {
    const configs = {
      creator: {
        label: "Creator",
        icon: FiAward,
        className: "creator",
      },
      moderator: {
        label: "Moderator",
        icon: FiShield,
        className: "moderator",
      },
      member: {
        label: "Member",
        icon: FiUsers,
        className: "member",
      },
    };
    return configs[role] || configs.member;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (role) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const currentUserRole = getCurrentUserRole();
  const canManageMembers =
    currentUserRole === "creator" || currentUserRole === "moderator";

  // Loading state
  if (loading && members.length === 0) {
    return (
      <div className="collab-comm-members-container">
        <div className="collab-comm-members-header">
          <div className="collab-comm-members-title">
            <FiUsers size={20} />
            <h3>{preview ? "Community Members" : "All Members"}</h3>
          </div>
        </div>
        <div className="collab-comm-members-loading">
          <div className="collab-comm-loading-spinner"></div>
          <p>Loading members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="collab-comm-members-container">
      {/* Header */}
      <div className="collab-comm-members-header">
        <div className="collab-comm-members-title">
          <FiUsers size={20} />
          <h3>{preview ? "Community Members" : "All Members"}</h3>
          <span className="collab-comm-members-count">({totalMembers})</span>
        </div>
      </div>

      {/* Controls - only in full view */}
      {!preview && (
        <div className="collab-comm-members-controls">
          <div className="collab-comm-search-box">
            <FiSearch size={16} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="collab-comm-filter-box">
            <FiFilter size={16} />
            <select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="creator">Creator</option>
              <option value="moderator">Moderators</option>
              <option value="member">Members</option>
            </select>
          </div>
        </div>
      )}

      {/* Members Grid */}
      <motion.div
        className="collab-comm-members-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {members.map((member) => {
          const roleConfig = getRoleConfig(member.role);
          const permissions = getPermissions(currentUserRole, member.role);
          const memberName =
            member.name ||
            `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
            "Unknown Member";
          const isActionLoading = actionLoading[member._id];
          const RoleIcon = roleConfig.icon;

          return (
            <div
              key={member._id}
              className={`collab-comm-member-card ${roleConfig.className}`}
            >
              {/* Avatar */}
              <div className="collab-comm-member-avatar">
                {member.profilePhotoUrl ? (
                  <img src={member.profilePhotoUrl} alt={memberName} />
                ) : (
                  <div className="collab-comm-member-avatar-placeholder">
                    {memberName ? memberName.charAt(0).toUpperCase() : "U"}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="collab-comm-member-info">
                <h4 className="collab-comm-member-name">{memberName}</h4>
                {member.username && (
                  <p className="collab-comm-member-username">@{member.username}</p>
                )}
              </div>

              {/* Role Badge */}
              <span className={`collab-comm-member-role-badge ${roleConfig.className}`}>
                <RoleIcon size={10} />
                {roleConfig.label}
              </span>

              {/* Joined Date */}
              {formatDate(member.joinedAt) && (
                <span className="collab-comm-member-joined">
                  {formatDate(member.joinedAt)}
                </span>
              )}

              {/* Three-Dot Menu */}
              {!preview && canManageMembers && (
                permissions.canPromote ||
                permissions.canDemote ||
                permissions.canRemove ||
                permissions.canWarn ||
                permissions.canMute ||
                permissions.canBan
              ) && (
                <div className="collab-comm-member-menu-wrapper">
                  <button
                    className={`collab-comm-member-menu-btn ${activeDropdown === member._id ? "active" : ""}`}
                    onClick={() => setActiveDropdown(activeDropdown === member._id ? null : member._id)}
                  >
                    <FiMoreVertical size={18} />
                  </button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {activeDropdown === member._id && (
                      <motion.div
                        className="collab-comm-member-dropdown"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.15 }}
                      >
                        {permissions.canPromote && (
                          <button
                            className="collab-comm-member-dropdown-item promote"
                            onClick={() => {
                              handlePromoteMember(member._id, memberName);
                              setActiveDropdown(null);
                            }}
                            disabled={isActionLoading}
                          >
                            <FiUserPlus size={14} />
                            Promote to Mod
                          </button>
                        )}

                        {permissions.canDemote && (
                          <button
                            className="collab-comm-member-dropdown-item demote"
                            onClick={() => {
                              handleDemoteMember(member._id, memberName);
                              setActiveDropdown(null);
                            }}
                            disabled={isActionLoading}
                          >
                            <FiChevronDown size={14} />
                            Demote to Member
                          </button>
                        )}

                        {(permissions.canPromote || permissions.canDemote) &&
                          (permissions.canWarn || permissions.canMute || permissions.canBan || permissions.canRemove) && (
                          <div className="collab-comm-member-dropdown-divider" />
                        )}

                        {permissions.canWarn && (
                          <button
                            className="collab-comm-member-dropdown-item warn"
                            onClick={() => {
                              setWarnModal({ isOpen: true, member });
                              setActiveDropdown(null);
                            }}
                          >
                            <FiAlertTriangle size={14} />
                            Warn User
                          </button>
                        )}

                        {permissions.canMute && (
                          memberRestrictions[member._id]?.isMuted ? (
                            <button
                              className="collab-comm-member-dropdown-item unmute"
                              onClick={() => {
                                handleUnmute(
                                  member._id,
                                  memberRestrictions[member._id].muteRestriction._id,
                                  memberName
                                );
                                setActiveDropdown(null);
                              }}
                              disabled={isActionLoading}
                            >
                              <FiVolume2 size={14} />
                              Unmute User
                            </button>
                          ) : (
                            <button
                              className="collab-comm-member-dropdown-item mute"
                              onClick={() => {
                                setMuteModal({ isOpen: true, member });
                                setActiveDropdown(null);
                              }}
                            >
                              <FiVolumeX size={14} />
                              Mute User
                            </button>
                          )
                        )}

                        {permissions.canBan && (
                          memberRestrictions[member._id]?.isBanned ? (
                            <button
                              className="collab-comm-member-dropdown-item unban"
                              onClick={() => {
                                handleUnban(
                                  member._id,
                                  memberRestrictions[member._id].banRestriction._id,
                                  memberName
                                );
                                setActiveDropdown(null);
                              }}
                              disabled={isActionLoading}
                            >
                              <FiUserCheck size={14} />
                              Unban User
                            </button>
                          ) : (
                            <button
                              className="collab-comm-member-dropdown-item ban"
                              onClick={() => {
                                setBanModal({ isOpen: true, member });
                                setActiveDropdown(null);
                              }}
                            >
                              <FiSlash size={14} />
                              Ban User
                            </button>
                          )
                        )}

                        {permissions.canRemove && (
                          <>
                            <div className="collab-comm-member-dropdown-divider" />
                            <button
                              className="collab-comm-member-dropdown-item remove"
                              onClick={() => {
                                handleRemoveMember(member._id, memberName);
                                setActiveDropdown(null);
                              }}
                              disabled={isActionLoading}
                            >
                              <FiUserMinus size={14} />
                              Remove
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          );
        })}
      </motion.div>

      {/* Empty state */}
      {members.length === 0 && !loading && (
        <motion.div
          className="collab-comm-members-empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FiUsers size={48} />
          <h4>No Members Found</h4>
          <p>
            {searchTerm || roleFilter !== "all"
              ? "Try adjusting your search or filters"
              : "This community doesn't have any members yet."}
          </p>
        </motion.div>
      )}

      {/* View all button for preview mode */}
      {preview && members.length >= 6 && (
        <motion.button
          className="collab-comm-view-all-btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiUsers size={16} />
          View All {totalMembers} Members
        </motion.button>
      )}

      {/* Pagination for full view */}
      {!preview && totalPages > 1 && (
        <div className="collab-comm-pagination">
          <motion.button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="collab-comm-pagination-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiChevronLeft size={16} />
            Previous
          </motion.button>

          <span className="collab-comm-pagination-info">
            Page {currentPage} of {totalPages}
          </span>

          <motion.button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="collab-comm-pagination-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Next
            <FiChevronRight size={16} />
          </motion.button>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      {/* Moderation Modals */}
      <WarnModal
        isOpen={warnModal.isOpen}
        memberId={warnModal.member?._id}
        memberName={
          warnModal.member?.name ||
          `${warnModal.member?.firstName || ""} ${warnModal.member?.lastName || ""}`.trim() ||
          "User"
        }
        communityId={communityId}
        onClose={() => setWarnModal({ isOpen: false, member: null })}
        onSuccess={fetchMembers}
      />

      <MuteModal
        isOpen={muteModal.isOpen}
        memberId={muteModal.member?._id}
        memberName={
          muteModal.member?.name ||
          `${muteModal.member?.firstName || ""} ${muteModal.member?.lastName || ""}`.trim() ||
          "User"
        }
        communityId={communityId}
        onClose={() => setMuteModal({ isOpen: false, member: null })}
        onSuccess={fetchMembers}
      />

      <BanModal
        isOpen={banModal.isOpen}
        memberId={banModal.member?._id}
        memberName={
          banModal.member?.name ||
          `${banModal.member?.firstName || ""} ${banModal.member?.lastName || ""}`.trim() ||
          "User"
        }
        communityId={communityId}
        onClose={() => setBanModal({ isOpen: false, member: null })}
        onSuccess={fetchMembers}
      />
    </div>
  );
};

export default CommunityMembers;
